import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  requestCalendarAccess,
  hasCalendarAccess,
  importFlightsFromCalendar,
  calendarFlightsToFlights,
  type CalendarFlight,
} from '../../services/calendarImport';
import { useApp } from '../../context/AppContext';
import { getCityName } from '../../utils/airports';
import { getAirline } from '../../utils/airlines';
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  Plane,
  ShieldAlert,
} from 'lucide-react';
import { haptic, hapticMedium } from '../../utils/haptics';

interface CalendarImportViewProps {
  onClose: () => void;
  onBack: () => void;
}

type Step = 'permission' | 'loading' | 'results' | 'error' | 'saving' | 'done';

export function CalendarImportView({ onClose, onBack }: CalendarImportViewProps) {
  const { currentUser, addFlights } = useApp();

  const [step, setStep] = useState<Step>('permission');
  const [flights, setFlights] = useState<CalendarFlight[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Check existing permission on mount
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      setError('Calendar import is only available on iOS.');
      setStep('error');
      return;
    }
    hasCalendarAccess().then((granted) => {
      if (granted) {
        loadFlights(month, year);
      }
      // else stay on 'permission' step
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestPermission = async () => {
    const granted = await requestCalendarAccess();
    if (granted) {
      loadFlights(month, year);
    } else {
      setError('Calendar access was denied. You can enable it in Settings > Rost & Found > Calendars.');
      setStep('error');
    }
  };

  const loadFlights = async (m: number, y: number) => {
    setStep('loading');
    setError(null);
    try {
      const result = await importFlightsFromCalendar(m, y, currentUser.airlineCode);
      setFlights(result);
      // Auto-select all
      setSelected(new Set(result.map((_, i) => i)));
      setStep('results');
    } catch (err) {
      console.warn('[CalendarImport] load failed:', err);
      setError('Failed to read calendar events. Please try again.');
      setStep('error');
    }
  };

  const handleMonthChange = (dir: -1 | 1) => {
    haptic();
    let m = month + dir;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
    loadFlights(m, y);
  };

  const toggleFlight = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleSave = async () => {
    hapticMedium();
    setStep('saving');
    try {
      const toSave = calendarFlightsToFlights(flights, currentUser.id, selected);
      const result = await addFlights(toSave);
      const inserted = result.inserted.length;
      const dupes = result.duplicatesSkipped;

      if (inserted === 0 && dupes > 0) {
        setSaveMessage(`All ${dupes} flight${dupes !== 1 ? 's were' : ' was'} already in your schedule.`);
      } else if (dupes > 0) {
        setSaveMessage(`${inserted} flight${inserted !== 1 ? 's' : ''} imported, ${dupes} duplicate${dupes !== 1 ? 's' : ''} skipped.`);
      } else {
        setSaveMessage(`${inserted} flight${inserted !== 1 ? 's' : ''} imported!`);
      }
      setStep('done');
    } catch {
      setError('Failed to save flights. Please try again.');
      setStep('error');
    }
  };

  const monthLabel = new Date(year, month - 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  // ── Permission screen ──
  if (step === 'permission') {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="w-14 h-14 rounded-full bg-accent/15 flex items-center justify-center">
          <CalendarDays size={28} className="text-accent" />
        </div>
        <div className="text-center">
          <h3 className="text-sm font-semibold text-slate-100">Import from Calendar</h3>
          <p className="text-xs text-slate-400 mt-1.5 max-w-[260px]">
            We'll scan your calendar for flight events and let you pick which ones to import.
          </p>
        </div>
        <button
          onClick={requestPermission}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark active:scale-[0.98] transition-all"
        >
          <Lock size={14} />
          Allow Calendar Access
        </button>
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-300">
          Back
        </button>
      </div>
    );
  }

  // ── Loading ──
  if (step === 'loading' || step === 'saving') {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <Loader2 size={24} className="text-accent animate-spin" />
        <p className="text-xs text-slate-400">
          {step === 'loading' ? 'Scanning calendar...' : 'Saving flights...'}
        </p>
      </div>
    );
  }

  // ── Error ──
  if (step === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center">
          <ShieldAlert size={24} className="text-red-400" />
        </div>
        <p className="text-sm text-slate-300 text-center max-w-[280px]">{error}</p>
        <div className="flex gap-2">
          <button
            onClick={() => loadFlights(month, year)}
            className="px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent-dark"
          >
            Retry
          </button>
          <button onClick={onBack} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">
            Back
          </button>
        </div>
      </div>
    );
  }

  // ── Done ──
  if (step === 'done') {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center">
          <Check size={24} className="text-green-400" />
        </div>
        <p className="text-sm text-slate-200 text-center">{saveMessage}</p>
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark"
        >
          Done
        </button>
      </div>
    );
  }

  // ── Results ──
  const selectedCount = selected.size;

  return (
    <div className="flex flex-col gap-3">
      {/* Month picker */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => handleMonthChange(-1)}
          className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm font-medium text-slate-200">{monthLabel}</span>
        <button
          onClick={() => handleMonthChange(1)}
          className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {flights.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <Plane size={24} className="text-slate-600" />
          <p className="text-xs text-slate-500 text-center">
            No flight events found in {monthLabel}.<br />
            Try a different month or check your calendar.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Found <span className="font-semibold text-slate-200">{flights.length}</span> flight{flights.length !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setSelected(new Set(flights.map((_, i) => i)))}
                className="text-xs text-accent font-medium"
              >
                Select all
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-slate-500 font-medium"
              >
                None
              </button>
            </div>
          </div>

          <div
            className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', transform: 'translateZ(0)' }}
          >
            {flights.map((f, i) => {
              const airline = getAirline(f.airlineCode);
              const isSelected = selected.has(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleFlight(i)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-accent/15 border border-accent/30'
                      : 'bg-slate-700/50 border border-slate-600/50 hover:bg-slate-700'
                  }`}
                >
                  {/* Checkbox */}
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-accent border-accent' : 'border-slate-500'
                    }`}
                  >
                    {isSelected && <Check size={12} className="text-white" />}
                  </div>

                  <Plane size={14} className="text-accent shrink-0" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-100 font-mono">
                        {f.flightNumber}
                      </span>
                      {airline && (
                        <span
                          className="text-[10px] px-1 py-0.5 rounded-full font-semibold"
                          style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                        >
                          {airline.name}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      <span className="font-mono">{f.departureAirport}</span>
                      {f.departureAirport && getCityName(f.departureAirport) !== f.departureAirport &&
                        ` ${getCityName(f.departureAirport)}`}
                      {' → '}
                      <span className="font-mono">{f.arrivalAirport}</span>
                      {f.arrivalAirport && getCityName(f.arrivalAirport) !== f.arrivalAirport &&
                        ` ${getCityName(f.arrivalAirport)}`}
                      {f.departureTime && <span className="font-mono"> · {f.departureTime}</span>}
                    </div>
                  </div>

                  <span className="text-xs text-slate-500 font-mono shrink-0">
                    {formatShort(f.date)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2 mt-1">
            <button
              onClick={handleSave}
              disabled={selectedCount === 0}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark disabled:opacity-50"
            >
              <Check size={16} />
              Import {selectedCount} flight{selectedCount !== 1 ? 's' : ''}
            </button>
            <button
              onClick={onBack}
              className="px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function formatShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
